import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { ItemCardView } from '../item-card/ItemCardView';
import { LocationLookupSearch } from './LocationLookupSearch';
import { LocationLookupView } from './LocationLookupView';

interface LocationLookupProps {
  onBack?: () => void;
}

/** Dyplenke inn (leses kun ved åpning) — adressefeltet skrives ikke, som i resten av appen. */
const readBinCodeFromUrl = (): string | null => {
  const value = new URLSearchParams(window.location.search).get('binCode');
  return value && value.trim() ? value.trim() : null;
};

/**
 * Lokasjonsoppslag (TW 36453034, Ariens P1): skann/skriv en lokasjon og se hva som ligger der,
 * åpne oppgaver og siste bevegelser. Read-only, åpent for alle (ingen egen tilgangsstyring, som varekortet).
 * Alltid aktivt lager — samme lokasjonskode kan finnes i flere lager.
 * Varekortet åpnes inne i modulen, så «tilbake» fra en vare går til lokasjonen.
 * Dyplenke inn: ?module=location-lookup&binCode=X.
 */
export const LocationLookup: React.FC<LocationLookupProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentWarehouse, warehouses } = useWarehouse();
  const [binCode, setBinCode] = useState<string | null>(readBinCodeFromUrl);
  const [itemNo, setItemNo] = useState<string | null>(null);

  // Dyplenken er brukt opp når den er lest (som i varekortet).
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('binCode')) return;
    url.searchParams.delete('binCode');
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);

  const isManager = user?.operatorGroupId === '2' || user?.operatorGroupId === '3';
  // Samme regel som lagerindikatoren i toppen: valgt lager, ellers brukerens eneste lager.
  // «Alle lagre» (flere lager, ingen valgt) gir null → søket ber om lagervalg.
  const warehouseId = currentWarehouse || (warehouses.length === 1 ? warehouses[0].warehouseID : null);

  const selectBin = useCallback((value: string) => { setItemNo(null); setBinCode(value); }, []);
  const clearBin = useCallback(() => { setItemNo(null); setBinCode(null); }, []);
  const closeItem = useCallback(() => setItemNo(null), []);

  if (binCode && itemNo) {
    return (
      <div className="min-h-full">
        <ItemCardView
          itemNo={itemNo}
          warehouseId={isManager ? null : warehouseId}
          isManager={isManager}
          onBack={closeItem}
          backLabel={t('warehouse.modules.locationLookup.backToLocation', { '0': binCode }, `Tilbake til ${binCode}`)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {binCode && warehouseId ? (
        <LocationLookupView warehouseId={warehouseId} binCode={binCode} onBack={clearBin} onOpenItem={setItemNo} />
      ) : (
        <LocationLookupSearch warehouseId={warehouseId} onSelect={selectBin} onBack={onBack} />
      )}
    </div>
  );
};
